import clsx from 'clsx'
import PropTypes from 'prop-types'

import { anim } from '../motion'
import { iso } from '../v3/iso'
import { Headline, useTextMode } from '../v8/Headline'

/**
 * Kleine Bausteine für die Extras der Version 7. Alle Zeitangaben beziehen
 * sich auf den Fortschritt des jeweiligen Extras (0..1), den V7 als `--l`
 * auf die Ebene des Extras schreibt.
 */

const range = PropTypes.arrayOf(PropTypes.number)

export function Anchor({ at3, dy = 0, children }) {
  const p = iso(...at3)
  return (
    <div className='off-anchor' style={{ left: p.x, top: p.y - dy }}>
      {children}
    </div>
  )
}

Anchor.propTypes = {
  at3: range.isRequired,
  dy: PropTypes.number,
  children: PropTypes.node
}

/** Etikett über einem Punkt in der Welt. */
export function Tag({ at3, dy = 86, dx = 0, show, hide, tone, children }) {
  return (
    <Anchor at3={at3} dy={dy}>
      <div
        className={clsx('off-tag', tone && `off-tag--${tone}`, 'kx-tag')}
        style={{
          marginLeft: dx,
          ...anim({ at: show, out: hide, y: 10, oy: -8, s: 0.6 })
        }}
      >
        {children}
      </div>
    </Anchor>
  )
}

Tag.propTypes = {
  at3: range.isRequired,
  dy: PropTypes.number,
  dx: PropTypes.number,
  show: range.isRequired,
  hide: range,
  tone: PropTypes.string,
  children: PropTypes.node
}

/** Sprechblase wie im Office. */
export function Bubble({ at3, dy = 84, dx = 0, show, hide, tone, children }) {
  return (
    <Anchor at3={at3} dy={dy}>
      <div
        className={clsx('off-bubble', tone && `off-bubble--${tone}`)}
        style={{
          marginLeft: dx,
          ...anim({ at: show, out: hide, y: 14, oy: -10, s: 0.7 })
        }}
      >
        {children}
      </div>
    </Anchor>
  )
}

Bubble.propTypes = Tag.propTypes

/**
 * Text unten links, gleiche Optik wie die Captions der Grundgeschichte.
 * Version 8 zeigt stattdessen `title` und `short` als Headline.
 */
export function Caption({ show, hide, label, title, short, children }) {
  const mode = useTextMode()
  if (mode === 'headline') {
    if (!title) return null
    // Wie in der Grundgeschichte nur kurz stehen lassen
    // Spätestens bei 0.95 gehen, sonst bleibt sie am Ende des Extras halb stehen
    const end = Math.min(show[1] + 0.12, hide ? hide[0] : 1, 0.95)
    return (
      <Headline
        show={show}
        hide={[end, end + 0.05]}
        title={title}
        line={short}
      />
    )
  }
  return (
    <div
      className='off-caption kx-caption'
      style={anim({ at: show, out: hide, y: 24, oy: -16 })}
    >
      <div className='off-eyebrow'>
        <span className='kx-extra-badge'>Extra</span> {label}
      </div>
      <p>{children}</p>
    </div>
  )
}

Caption.propTypes = {
  show: range.isRequired,
  hide: range,
  label: PropTypes.string.isRequired,
  title: PropTypes.string,
  short: PropTypes.string,
  children: PropTypes.node
}

/** Weg [{ x, y }] mit Zeitpunkten von `from` bis `to`, gleichmäßiges Tempo. */
export function timed(points, from, to) {
  const dist = [0]
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x
    const dy = points[i].y - points[i - 1].y
    dist.push(dist[i - 1] + Math.hypot(dx, dy))
  }
  const total = dist[dist.length - 1] || 1
  return points.map((p, i) => ({
    at: from + ((to - from) * dist[i]) / total,
    x: p.x,
    y: p.y
  }))
}
